import { useEffect, useState } from "react";

import { hasCounterApp as hasApp } from "../domain/counterApp";
import { todayStr } from "../domain/date";
import { isAndroid } from "../lib/device";
import { pingSample } from "../lib/appBridge";
import { useKaya } from "../store/kayaStore";
import { Button } from "./Button";
import { I } from "./icons";

/** "3 min ago" for a stamp, or null for never. Coarse on purpose: this is read, not computed. */
export function ago(at: number | null, now = Date.now()): string | null {
  if (!at) return null;
  const mins = Math.max(0, Math.round((now - at) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  return h < 24 ? `${h} h ago` : "more than a day ago";
}

/**
 * When the phone last reported, and a button that asks it again.
 *
 * The scheduled sample is a quarter of an hour apart at best, and on some phones the schedule
 * dies when the app is swiped away — which made "the steps are stale" and "the steps are wrong"
 * indistinguishable. Saying when the last figure arrived separates the two, and the button is the
 * fix for the first without a trip to the counter app's own settings screen. (ADR-027)
 */
export function StepsRefresh({ compact }: { compact?: boolean }) {
  const { user, stepsReportedAt, refreshSteps, online, S } = useKaya();
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);

  // Only a phone that has the counter app can be asked: without it the ping would go to nothing,
  // and on a phone with no handler for it the browser falls back to reloading the page.
  const hasCounterApp = hasApp(S, todayStr(), isAndroid());

  // "3 min ago" needs to keep being true.
  useEffect(() => {
    const t = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(t);
  }, []);

  if (!user) return null;

  const refresh = () => {
    setBusy(true);
    // The phone reads and posts on its own timetable, a second or two after being asked; ask the
    // server once now for whatever it already has, and again once the phone has had its moment.
    if (hasCounterApp) pingSample();
    void refreshSteps({ force: true });
    window.setTimeout(() => void refreshSteps({ force: true }), 6_000);
    window.setTimeout(() => {
      void refreshSteps({ force: true });
      setBusy(false);
    }, 20_000);
  };

  const when = ago(stepsReportedAt);

  return (
    <div className="row stepsmeta">
      <span className="muted grow">
        {when ? `Phone reported ${when}` : "No count from the phone yet"}
        {!online ? " · offline" : ""}
        {/* Only the Android app can be asked; a shortcut posts on its own timetable. */}
        {!hasCounterApp && !compact ? " · shows whatever your phone last posted" : ""}
      </span>
      <Button variant="ghost" icon={I.refresh} onClick={refresh} busy={busy} disabled={!online}>
        {busy ? "Asking…" : compact ? "Refresh" : "Refresh steps"}
      </Button>
    </div>
  );
}
